import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import MovieDetailsPage from "./MovieDetailsPage";
import SeriesDetailsPage from "./SeriesDetailsPage";
import { fetchSeriesDetail } from "../../redux/content";
import "../../components/styles/movieDetailsPage.scss";

/**
 * DetailsPage — dispatcher for /details/:id.
 *
 * The same route serves both movies (`/movies/{id}`) and series (`/series/{id}`),
 * which live in separate id spaces with different response shapes. This component
 * decides which detail page to render:
 *
 *   1. Fast path: poster clicks pass `state.contentType` ("movie" | "series"),
 *      taken from the VOD content item's `type` field. No extra request.
 *   2. Fallback (direct URL / refresh / no state): probe `/series/{id}`. The
 *      series endpoint returns ok ONLY for real series ids, so a hit ⇒ series,
 *      a miss ⇒ movie. (A movie id never resolves at /series/{id}.)
 */
const DetailsPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const stateType = location.state?.contentType;

  // Normalize the nav-state hint to our two kinds, else null (needs probing).
  const hintedKind =
    stateType === "series" ? "series" : stateType === "movie" ? "movie" : null;

  const [kind, setKind] = useState(hintedKind);

  // Reset when the id (or hint) changes so back/forward + in-page navigation work.
  useEffect(() => {
    setKind(hintedKind);
  }, [id, hintedKind]);

  // Probe only when we have no reliable hint.
  useEffect(() => {
    if (kind || !id) return;
    let active = true;
    console.log("[DetailsPage] No contentType hint — probing /series/", id);
    fetchSeriesDetail(id).then((data) => {
      if (!active) return;
      const resolved = data ? "series" : "movie";
      console.log("[DetailsPage] Probe result for", id, "→", resolved);
      setKind(resolved);
    });
    return () => { active = false; };
  }, [id, kind]);

  if (!kind) {
    return (
      <div className="md-page">
        <Navbar />
        <div className="md-page__loader">
          <div className="md-page__loader-ring" />
        </div>
        <Footer />
      </div>
    );
  }

  // key={id} so the underlying page fully remounts when switching content.
  return kind === "series"
    ? <SeriesDetailsPage key={id} />
    : <MovieDetailsPage key={id} />;
};

export default DetailsPage;
